import { useTranslation } from 'react-i18next'
import { Shield, RefreshCw, CheckCircle, AlertTriangle } from 'lucide-react'
import { useChainOfCustody, useChainVerification } from '../../../hooks'

interface ChainTimelineViewProps {
  caseId: string
}

const EVENT_TYPE_COLOURS: Record<string, string> = {
  collection: 'bg-blue-100 text-blue-700 dark:bg-blue-950/50 dark:text-blue-400',
  transfer: 'bg-amber-100 text-amber-700 dark:bg-amber-950/50 dark:text-amber-400',
  storage: 'bg-slate-100 text-slate-700 dark:bg-slate-800 dark:text-slate-400',
  access: 'bg-green-100 text-green-700 dark:bg-green-950/50 dark:text-green-400',
  analysis: 'bg-purple-100 text-purple-700 dark:bg-purple-950/50 dark:text-purple-400',
  export: 'bg-cyan-100 text-cyan-700 dark:bg-cyan-950/50 dark:text-cyan-400',
  disposal: 'bg-red-100 text-red-700 dark:bg-red-950/50 dark:text-red-400',
  acquired: 'bg-emerald-100 text-emerald-700 dark:bg-emerald-950/50 dark:text-emerald-400',
  received: 'bg-amber-100 text-amber-700 dark:bg-amber-950/50 dark:text-amber-400',
  transferred: 'bg-indigo-100 text-indigo-700 dark:bg-indigo-950/50 dark:text-indigo-400',
  verified: 'bg-green-100 text-green-700 dark:bg-green-950/50 dark:text-green-400',
  exported: 'bg-violet-100 text-violet-700 dark:bg-violet-950/50 dark:text-violet-400',
}

export function ChainTimelineView({ caseId }: ChainTimelineViewProps): React.ReactElement {
  const { t } = useTranslation('cases')
  const { entries, count, loading, error, refetch } = useChainOfCustody(caseId)
  const { verification, loading: verifying, verify } = useChainVerification(caseId)

  if (loading) {
    return <LoadingState message={t('custody.loadingCustody')} />
  }

  if (error) {
    return (
      <ErrorState
        error={error}
        onRetry={() => {
          void refetch()
        }}
        retryLabel={t('actions.tryAgain')}
      />
    )
  }

  return (
    <div className="space-y-6">
      <TimelineHeader
        count={count}
        verifying={verifying}
        onVerify={() => {
          void verify()
        }}
        onRefresh={() => {
          void refetch()
        }}
      />
      {verification ? <VerificationBanner verification={verification} /> : null}
      <TimelineEntries entries={entries} />
    </div>
  )
}

function LoadingState({ message }: { message: string }): React.ReactElement {
  return (
    <div className="flex h-64 items-center justify-center">
      <div className="flex flex-col items-center gap-3">
        <RefreshCw className="h-6 w-6 animate-spin text-[#4e5e82]" />
        <p className="text-sm text-slate-500 dark:text-slate-400">{message}</p>
      </div>
    </div>
  )
}

function ErrorState({
  error,
  onRetry,
  retryLabel,
}: {
  error: string
  onRetry: () => void
  retryLabel: string
}): React.ReactElement {
  return (
    <div className="flex h-64 items-center justify-center">
      <div className="text-center">
        <p className="text-red-600 dark:text-red-400">{error}</p>
        <button
          onClick={onRetry}
          className="mt-4 text-sm text-lime-600 hover:underline dark:text-lime-400"
        >
          {retryLabel}
        </button>
      </div>
    </div>
  )
}

interface TimelineHeaderProps {
  count: number
  verifying: boolean
  onVerify: () => void
  onRefresh: () => void
}

function TimelineHeader({
  count,
  verifying,
  onVerify,
  onRefresh,
}: TimelineHeaderProps): React.ReactElement {
  const { t } = useTranslation('cases')

  return (
    <div className="flex items-center justify-between">
      <div className="flex items-center gap-3">
        <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-lime-100 dark:bg-lime-950/50">
          <Shield className="h-5 w-5 text-lime-600 dark:text-lime-400" />
        </div>
        <div>
          <h2 className="text-lg font-semibold text-slate-900 dark:text-slate-100">
            {t('custody.title')}
          </h2>
          <p className="text-sm text-slate-500 dark:text-slate-400">
            {t('custody.entryCount', { count })}
          </p>
        </div>
      </div>
      <div className="flex items-center gap-3">
        <button
          onClick={onVerify}
          disabled={verifying}
          className="inline-flex items-center gap-2 rounded-lg border border-slate-300 px-4 py-2 text-sm font-medium text-slate-700 hover:bg-slate-50 disabled:opacity-50 dark:border-slate-700 dark:text-slate-300 dark:hover:bg-slate-800"
        >
          {verifying ? (
            <RefreshCw className="h-4 w-4 animate-spin" />
          ) : (
            <Shield className="h-4 w-4" />
          )}
          {t('custody.verifyChain')}
        </button>
        <button
          onClick={onRefresh}
          className="rounded-lg border border-slate-300 p-2 text-slate-500 hover:bg-slate-100 dark:border-slate-700 dark:text-slate-400 dark:hover:bg-slate-800"
        >
          <RefreshCw className="h-4 w-4" />
        </button>
      </div>
    </div>
  )
}

interface VerificationResult {
  isValid: boolean
  entriesVerified: number
  totalEntries: number
  entriesValid: number
  entriesInvalid: number
}

function VerificationBanner({
  verification,
}: {
  verification: VerificationResult
}): React.ReactElement {
  const { t } = useTranslation('cases')
  const valid = verification.isValid

  return (
    <div
      className={`rounded-lg p-4 ${valid ? 'border border-green-200 bg-green-50 dark:border-green-900 dark:bg-green-950/30' : 'border border-red-200 bg-red-50 dark:border-red-900 dark:bg-red-950/30'}`}
    >
      <div className="flex items-center gap-3">
        {valid ? (
          <CheckCircle className="h-5 w-5 text-green-600 dark:text-green-400" />
        ) : (
          <AlertTriangle className="h-5 w-5 text-red-600 dark:text-red-400" />
        )}
        <div>
          <p
            className={`font-medium ${valid ? 'text-green-800 dark:text-green-200' : 'text-red-800 dark:text-red-200'}`}
          >
            {valid ? t('custody.chainVerified') : t('custody.chainIssues')}
          </p>
          <p className="text-sm text-slate-600 dark:text-slate-400">
            {t('custody.verificationSummary', {
              verified: verification.entriesVerified,
              total: verification.totalEntries,
              valid: verification.entriesValid,
              invalid: verification.entriesInvalid,
            })}
          </p>
        </div>
      </div>
    </div>
  )
}

interface CocEntry {
  id: string
  eventType: string
  evidenceType: string
  evidenceName: string
  handlerName: string
  handlerOrganisation: string | null
  eventTimestamp: string
  hashAlgorithm: string | null
  hashValue: string | null
  notes: string | null
}

function TimelineEntries({ entries }: { entries: CocEntry[] }): React.ReactElement {
  const { t } = useTranslation('cases')

  if (entries.length === 0) {
    return (
      <div className="rounded-lg border border-dashed border-slate-300 p-8 text-center dark:border-slate-700">
        <Shield className="mx-auto h-12 w-12 text-slate-300 dark:text-slate-600" />
        <p className="mt-4 text-slate-600 dark:text-slate-400">{t('custody.noEntriesYet')}</p>
        <p className="mt-2 text-xs text-slate-500">{t('custody.autoCreatedHint')}</p>
      </div>
    )
  }

  return (
    <div className="relative">
      <div className="absolute top-0 bottom-0 left-6 w-px bg-slate-200 dark:bg-slate-700" />
      <div className="space-y-4">
        {entries.map((entry) => (
          <TimelineEntry key={entry.id} entry={entry} />
        ))}
      </div>
    </div>
  )
}

function TimelineEntry({ entry }: { entry: CocEntry }): React.ReactElement {
  const { t } = useTranslation('cases')
  const bgColour = EVENT_TYPE_COLOURS[entry.eventType]?.split(' ')[0] ?? 'bg-slate-200'

  return (
    <div className="relative flex gap-4 pl-12">
      <div
        className={`absolute left-4 h-4 w-4 rounded-full border-2 border-white dark:border-slate-900 ${bgColour}`}
      />
      <div className="flex-1 rounded-lg border border-slate-200 bg-white p-4 dark:border-slate-700 dark:bg-slate-900">
        <div className="flex items-start justify-between">
          <div>
            <div className="flex items-center gap-2">
              <span
                className={`inline-flex rounded px-2 py-0.5 text-xs font-medium capitalize ${String(EVENT_TYPE_COLOURS[entry.eventType] ?? EVENT_TYPE_COLOURS['storage'])}`}
              >
                {entry.eventType}
              </span>
              <span className="inline-flex rounded bg-slate-100 px-2 py-0.5 text-xs font-medium text-slate-700 dark:bg-slate-800 dark:text-slate-400">
                {entry.evidenceType}
              </span>
            </div>
            <h4 className="mt-1 font-medium text-slate-900 dark:text-slate-100">
              {entry.evidenceName}
            </h4>
            <p className="text-sm text-slate-500 dark:text-slate-400">
              {t('custody.handledBy')} {entry.handlerName}
              {entry.handlerOrganisation === null ? '' : ` (${entry.handlerOrganisation})`}
            </p>
          </div>
          <div className="text-right">
            <p className="text-sm text-slate-500 dark:text-slate-400">
              {new Date(entry.eventTimestamp).toLocaleDateString()}
            </p>
            <p className="text-xs text-slate-400 dark:text-slate-500">
              {new Date(entry.eventTimestamp).toLocaleTimeString()}
            </p>
          </div>
        </div>
        {entry.hashValue ? (
          <div className="mt-3 border-t border-slate-100 pt-3 dark:border-slate-800">
            <p className="truncate font-mono text-xs text-slate-500 dark:text-slate-400">
              {entry.hashAlgorithm}: {entry.hashValue}
            </p>
          </div>
        ) : null}
        {entry.notes ? (
          <p className="mt-2 text-xs text-slate-500 dark:text-slate-400">{entry.notes}</p>
        ) : null}
      </div>
    </div>
  )
}
